// A failed action's toast body (ported from vouchers): the friendly summary,
// the field errors no field shows, then a Details disclosure with what
// support needs, copyable.

import { Check, ChevronDown, Copy } from "lucide-react";
import { useState } from "react";
import { ApiError, type FieldErrorSpec, NO_CODE } from "@/api/error";
import { friendlyMessage } from "@/api/friendly-error";

const UNMAPPED_LIMIT = 5;

export interface ErrorToastContentProps {
    error: unknown;
    /** Field errors the form couldn't put on a field; listed so the user sees what failed. */
    unmapped?: ReadonlyArray<FieldErrorSpec>;
}

export function ErrorToastContent({ error, unmapped }: ErrorToastContentProps) {
    const summary = friendlyMessage(error);
    const api = error instanceof ApiError ? error : undefined;
    // NO_CODE is the client's own stand-in, not a code the server sent.
    const code = api && api.code !== NO_CODE ? api.code : undefined;
    // The body's requestId, else the X-Request-Id header; absent without either.
    const requestId = api?.requestId;
    const visibleUnmapped = unmapped?.slice(0, UNMAPPED_LIMIT) ?? [];
    const hiddenUnmapped = (unmapped?.length ?? 0) - visibleUnmapped.length;
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState<string | null>(null);

    function copy(value: string, key: string) {
        navigator.clipboard
            .writeText(value)
            .then(() => {
                setCopied(key);
                setTimeout(() => setCopied(null), 1500);
            })
            .catch(() => {
                // Clipboard unavailable: the icon stays Copy.
            });
    }

    const rows = [
        code && { key: "code", label: "Code", value: code },
        requestId && { key: "request-id", label: "Request ID", value: requestId },
    ].filter((row): row is { key: string; label: string; value: string } => Boolean(row));

    return (
        <div className="space-y-1.5">
            <p className="text-sm">{summary}</p>
            {visibleUnmapped.length > 0 && (
                <ul className="space-y-0.5 text-xs text-muted-foreground">
                    {visibleUnmapped.map((field) => (
                        <li key={`${field.path}:${field.code}`}>
                            <span className="font-mono">{field.path}</span>: {field.message}
                        </li>
                    ))}
                    {hiddenUnmapped > 0 && <li className="italic">+{hiddenUnmapped} more</li>}
                </ul>
            )}
            {rows.length > 0 && (
                <div>
                    <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => setOpen((value) => !value)}
                        className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                    >
                        <ChevronDown
                            className={`size-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                        />
                        Details
                    </button>
                    {open && (
                        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
                            {rows.map((row) => (
                                <div key={row.key} className="contents">
                                    <dt className="self-center font-medium">{row.label}</dt>
                                    <dd className="flex min-w-0 items-center gap-1.5">
                                        <span className="truncate font-mono">{row.value}</span>
                                        <button
                                            type="button"
                                            onClick={() => copy(row.value, row.key)}
                                            className="shrink-0 transition-colors hover:text-foreground"
                                            aria-label={`Copy ${row.label.toLowerCase()}`}
                                        >
                                            {copied === row.key ? (
                                                <Check className="size-3" />
                                            ) : (
                                                <Copy className="size-3" />
                                            )}
                                        </button>
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    )}
                </div>
            )}
        </div>
    );
}
